// Car-dealership showroom geometry. Phaser-free so it loads under `node --test`.
//
// The showroom is ONE baked image (bg_car_dealer.webp, 3344×941 = [left half 1672][right
// half 1672], see scripts/compose_showroom.py) scaled to height 720, exactly the way the
// street is baked — world x = srcX * DEALER_SCALE.
//
// Traced against the real art (2026-08-28) by measuring the rope-stanchion posts and the
// display podium in both halves at a 50 px grid, then re-checking the numbers over the
// finished composite at world scale:
//
//   left half   podium x 450…1672 src, front edge y ≈ 600 src
//               bays (post → post) 478…762, 848…1152, 1240…1552 src
//   right half  podium x 70…1200 src, same front edge
//               bays 120…405, 535…785, 900…1180 src (+1672 in composite coords)
//               service corner (potted plant, tyre rack, red tool cart) 1230…1500 src
//               doormat 1500…1600 src, glass street door 1565 src → the right edge
//
// So the ENTRANCE IS ON THE RIGHT, not on the left as the task brief guessed: the left
// half opens on the reception desk/lounge (no door at all) and the only door in the art is
// the glass one next to the service corner. That also matches the backend, which parks
// `npc:mechanic` at world x 2350 (catalog.py NPC_DEFS) — dead centre of the tyre rack.
// You therefore walk IN at the right edge and browse leftwards down the showroom.

import { carFrontWheelOffset } from '../ui/carCatalog.mjs';

export const DEALER_SRC_W = 3344;
export const DEALER_SRC_H = 941;
export const DEALER_SCALE = 720 / DEALER_SRC_H;                              // 0.76514
export const DEALER_WORLD_WIDTH = Math.round(DEALER_SRC_W * DEALER_SCALE);   // 2559

export function dealerX(srcX) { return Math.round(srcX * DEALER_SCALE); }

// Walker floor: `floorY` is the highest (smallest) sprite-CENTRE y, so the feet land at
// FLOOR_Y + 120 = 560 (src y ≈ 732) — on the tiled floor a comfortable step in front of
// the podium, whose front edge is at world y ≈ 459. She can still walk down to y 630.
export const FLOOR_Y = 440;

// The podium's front lip, world y — the line every car's tyres stand on. Measured off the
// baked art (src y ≈ 600 · DEALER_SCALE).
export const PODIUM_Y = 459;

// Six display bays. You walk IN at the right edge (see above) and browse leftwards, so the
// cars are ordered CHEAPEST ON THE RIGHT: the first thing you meet is the 8 000 zł Panda and
// the Corvette is the long walk down the hall. `x` is the bay centre nudged so neighbouring
// cars (front portraits are ~300 px wide) never touch.
//
// `y` is the sprite CENTRE, and it is NOT the same for every model: the front portraits pad
// their 400 px canvas by different amounts (CAR_FRONT_CONTENT_BOTTOM) and are sized per model
// (carDisplayHeight), so one shared y left each car hovering its own distance above the
// podium. Deriving it from the podium line puts all six sets of tyres on PODIUM_Y.
const BAY_X = [470, 775, 1080, 1475, 1785, 2090];
const ORDER = ['c8', 'model3', 'q5', 't03', 'bclass', 'panda'];

export function spotY(model) { return PODIUM_Y - carFrontWheelOffset(model); }

export const SPOTS = ORDER.map((model, i) => ({ model, x: BAY_X[i], y: spotY(model) }));

// Where the mechanic stands — mirror of backend catalog.py NPC_DEFS `npc:mechanic`. The
// live NPC is what the scene actually measures against (SERVICE uses NEAR to his sprite);
// this is here so the layout test can prove he lands in the service corner.
export const MECHANIC = { x: 2350, y: 470 };

// Walking past this x steps out onto the street (the doormat in front of the glass door).
// The walker's own bound is DEALER_WORLD_WIDTH − 60 = 2499, so the band is reachable.
export const EXIT_ZONE = { xMin: 2470 };

// Where you land coming in from the street: just inside the door, clear of the trigger.
export const SPAWN = { x: 2390, y: 560 };

export function inExitZone(x) { return x >= EXIT_ZONE.xMin; }
